/**
 * Laravel Echo 설정
 *
 * @CODE:API-201 | SPEC: olulo-mx-admin/.claude/specs/SPEC-201-notification-api.md
 */
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import type { ChannelAuthorizationCallback } from 'pusher-js';
import { logger } from '@/utils/logger';

declare global {
  interface Window {
    Pusher: typeof Pusher;
    Echo: Echo<'reverb'> | undefined;
  }
}

// Pusher를 전역으로 등록 (Laravel Echo 요구사항)
if (typeof window !== 'undefined') {
  window.Pusher = Pusher;
}

// 현재 인증 토큰을 추적하여 재초기화 필요 여부 판단
let currentAuthToken: string | undefined;

/**
 * Laravel Echo 인스턴스 초기화
 *
 * @param authToken - 인증 토큰 (Sanctum 세션 기반이면 생략 가능)
 * @param forceReinitialize - true이면 기존 인스턴스를 disconnect하고 새로 생성
 */
export function initializeEcho(authToken?: string, forceReinitialize = false): Echo<'reverb'> {
  if (typeof window === 'undefined') {
    throw new Error('Echo can only be initialized in browser environment');
  }

  // 인증 토큰이 변경되었거나 강제 재초기화 요청 시 기존 인스턴스 정리
  const tokenChanged = authToken !== currentAuthToken;
  if (window.Echo && (forceReinitialize || tokenChanged)) {
    logger.info('[Echo] Reinitializing due to token change or force flag');
    window.Echo.disconnect();
    window.Echo = undefined;
  }

  // 이미 초기화되어 있으면 기존 인스턴스 반환
  if (window.Echo) {
    return window.Echo;
  }

  const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
  if (!apiEndpoint) {
    throw new Error('[Echo] VITE_API_ENDPOINT가 설정되지 않았습니다. .env 파일을 확인해주세요.');
  }

  const reverbKey = import.meta.env.VITE_REVERB_APP_KEY;
  const reverbHost = import.meta.env.VITE_REVERB_HOST;
  if (!reverbKey || !reverbHost) {
    const missing = !reverbKey ? 'VITE_REVERB_APP_KEY' : 'VITE_REVERB_HOST';
    throw new Error(`[Echo] ${missing} is not configured`);
  }

  const echoInstance = new Echo({
    broadcaster: 'reverb',
    key: reverbKey,
    wsHost: reverbHost,
    wsPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
    wssPort: Number(import.meta.env.VITE_REVERB_PORT) || 443,
    forceTLS: import.meta.env.VITE_REVERB_SCHEME === 'https',
    enabledTransports: ['ws', 'wss'],
    authEndpoint: `${apiEndpoint}/broadcasting/auth`,
    authorizer: createBroadcastAuthorizer(apiEndpoint),
    // Pusher checks for the presence of auth before reading its members.
    // Omit it for cookie auth instead of passing an undefined value.
    ...(authToken ? { auth: { headers: { Authorization: `Bearer ${authToken}` } } } : {}),
  });

  window.Echo = echoInstance;
  currentAuthToken = authToken;
  return echoInstance;
}

/**
 * Echo 인스턴스 정리
 */
export function disconnectEcho(): void {
  if (typeof window !== 'undefined' && window.Echo) {
    window.Echo.disconnect();
    window.Echo = undefined;
  }
  currentAuthToken = undefined;
}

/**
 * 크로스 오리진 Sanctum 세션용 채널 인증 authorizer
 *
 * pusher-js 기본 authorizer 는 쿠키를 싣지 않아(withCredentials 미설정)
 * admin 으로 세션이 전달되지 않는다. fetch + credentials: 'include' 로 대체한다.
 */
export function createBroadcastAuthorizer(apiEndpoint: string) {
  return (channel: { name: string }) => ({
    authorize: (
      socketId: string,
      callback: ChannelAuthorizationCallback
    ): void => {
      fetch(`${apiEndpoint}/broadcasting/auth`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ socket_id: socketId, channel_name: channel.name }),
      })
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(`[Echo] 채널 인증 실패 (${response.status})`);
          }
          callback(null, await response.json());
        })
        .catch((error: Error) => {
          logger.error('[Echo] Channel authorization failed:', error);
          callback(error, null);
        });
    },
  });
}

/**
 * 현재 Echo 인스턴스 가져오기
 */
export function getEcho(): Echo<'reverb'> | undefined {
  if (typeof window !== 'undefined') {
    return window.Echo;
  }
  return undefined;
}

/** 소켓이 실제로 연결된 상태인지 — 인스턴스 존재만으로는 채널 인증/연결 실패를 구분할 수 없다 */
export function isEchoConnected(): boolean {
  const echo = getEcho();
  if (!echo) return false;
  try {
    // laravel-echo 2.2.6 reverb broadcaster 는 PusherConnector 를 사용하며,
    // connector.pusher(pusher-js Pusher).connection(ConnectionManager).state 로 연결 상태를 노출한다
    const connector = echo.connector as { pusher?: { connection?: { state?: string } } };
    return connector.pusher?.connection?.state === 'connected';
  } catch {
    return false;
  }
}
